import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { filter, map, switchMap, take } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private token: string | null = null;
    private tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private isFetchingToken = false;

    constructor(private authService: AuthService) {}

    /**
     * Intercepts HTTP requests to add authorization tokens to specific URLs.
     *
     * This method checks if the request URL is one of the specified endpoints
     * that require token authentication. If the current token is valid and not
     * expired, it adds the token to the request headers. If the token is expired
     * or unavailable, the request is queued until a new token is fetched.
     *
     * @param req - The outgoing HTTP request to intercept.
     * @param next - The next interceptor in the chain.
     * @returns An observable of the HTTP event, either with the token added or
     * directly passed through if not required.
     */

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Äbin använder endast localhost, utv och prod-miljö
        if (req.url.includes('localhost:54654') || req.url.includes('sksapiutv') || req.url.includes('api.skogsstyrelsen.se')) {
            if (this.token && !this.authService.tokenExpired(this.authService.tokenDecoded!)) {
                return next.handle(this.addToken(req, this.token));
            } else {
                return this.queueRequest(req, next);
            }
        } else {
            return next.handle(req);
        }
    }

    private queueRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isFetchingToken) {
            this.isFetchingToken = true;
            this.authService
                .login()
                .pipe(take(1))
                .subscribe((token) => {
                    this.token = token;
                    this.tokenSubject.next(token);
                    this.isFetchingToken = false;
                });
        }

        return this.tokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addToken(req, token!)))
        );
    }

    private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
        return req.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
        });
    }
}
